import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { accessoriesSchema } from "../../schemas/accessoriesSchema";
import { FileText, XCircle } from "lucide-react";
import LoadingButton from "../../components/LoadingButton";
import { useState, useEffect } from "react";
import { useFileUpload } from "../../hooks/useFileUplode";

export default function AccessoriesForm({
  mode,
  defaultValues = {},
  onSubmit,
  onCancel,
  submissionLoading = false,
  AccessoriesData = {},
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const { fileUpload } = useFileUpload();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(accessoriesSchema),
    defaultValues: defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const isActive = watch("is_active");

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium mb-1">Title</label>
        <input
          {...register("title")}
          readOnly={mode === "view"}
          placeholder="Enter title"
          className="pl-3 pr-3 py-2 border rounded w-full"
        />
        {errors.title && (
          <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>
        )}
      </div>

      {/* Price */}
      <div>
        <label className="block text-sm font-medium mb-1">Price</label>
        <input
          type="number"
          {...register("Price")}
          readOnly={mode === "view"}
          placeholder="Enter price"
          className="pl-3 pr-3 py-2 border rounded w-full"
        />
        {errors.Price && (
          <p className="text-red-500 text-xs mt-1">{errors.Price.message}</p>
        )}
      </div>

      {/* Image Upload / Preview */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {mode === "view" ? "Image" : "Upload Image"}
        </label>
        <Controller
          name="image"
          control={control}
          render={({ field }) => (
            <>
              {/* Preview existing or uploaded image */}
              {field.value && (
                <div className="mb-2 relative inline-block">
                  <img
                    src={field.value}
                    alt="Uploaded"
                    className="max-w-full max-h-64 rounded"
                  />
                  {mode !== "view" && (
                    <XCircle
                      className="absolute top-1 right-1 cursor-pointer text-red-500"
                      size={20}
                      onClick={() => field.onChange("")}
                    />
                  )}
                </div>
              )}

              {/* Only show file input when not in view mode */}
              {mode !== "view" && (
                <>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.tiff,.svg"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const allowedTypes = [
                        "image/jpeg",
                        "image/jpg",
                        "image/png",
                        "image/gif",
                        "image/bmp",
                        "image/webp",
                        "image/tiff",
                        "image/svg+xml",
                      ];
                      if (!allowedTypes.includes(file.type)) {
                        setUploadError(
                          "Only JPG, JPEG, PNG, GIF, BMP, WEBP, TIFF, SVG files are allowed"
                        );
                        return;
                      }
                      setUploading(true);
                      setUploadError(null);
                      try {
                        const formData = new FormData();
                        formData.append("document", file);
                        const response = await fileUpload(formData);
                        const uploadedUrl = response?.url || response?.data?.url || "";
                        field.onChange(uploadedUrl);
                      } catch (error) {
                        setUploadError(error.message || "File upload failed");
                      } finally {
                        setUploading(false);
                      }
                    }}
                    disabled={uploading}
                    className="border rounded w-full py-2 px-3"
                  />
                  {uploading && (
                    <p className="text-blue-500 text-xs mt-1">Uploading...</p>
                  )}
                  {uploadError && (
                    <p className="text-red-500 text-xs mt-1">{uploadError}</p>
                  )}
                </>
              )}

              {/* No image in view mode */}
              {!field.value && mode === "view" && (
                <p className="text-gray-500 text-sm">No image uploaded</p>
              )}
            </>
          )}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border rounded text-gray-700"
        >
          {mode === "view" ? "Close" : "Cancel"}
        </button>
        {mode !== "view" && (
          <button
            type="submit"
            disabled={submissionLoading}
            className="px-4 py-2 bg-primary text-white rounded flex items-center justify-center gap-2"
          >
            {submissionLoading ? (
              <LoadingButton />
            ) : (
              <>{mode === "edit" ? "Save Changes" : "Create Page"}</>
            )}
          </button>
        )}
      </div>
    </form>
  );
}
